import {
  Button,
  Checkbox,
  Collapse,
  FormControl,
  FormControlLabel,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  SelectChangeEvent,
  Switch,
  TextField,
} from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Claim, NamedEntity } from "../shared/CaseModel";
import { DialogSection, DialogShell } from "./general/DialogShell";

export interface IssueEditorProps {
  // parent
  parentClaim: Claim;
  title?: string;
  defendants?: NamedEntity[];
  parties?: NamedEntity[];
  generic?: boolean;
  // is dialog open?
  open: boolean;
  onClose: (claimTemplate?: {
    title: string;
    defendants: NamedEntity[];
    generic?: boolean;
  }) => void;
}

function isValidClaimTitle(claimTitle?: string): boolean {
  return claimTitle !== undefined && claimTitle.trim().length > 0;
}

/** Room for the lines that say what the two kinds of issue are, in px. */
const WIDTH = 560;

export const IssueEditorDialog = ({
  parentClaim,
  open,
  onClose,
  title: initialTitle = "",
  generic: initialGeneric,
  defendants = [],
  parties = [],
}: IssueEditorProps) => {
  const { t } = useTranslation(undefined, {
    keyPrefix: "case.claims.issueDialog",
  });
  const [title, setTitle] = useState(initialTitle);
  const [generic, setGeneric] = useState(initialGeneric);
  const [defendantIds, setDefendantIds] = useState<string[]>(
    defendants.map((defendant) => defendant.id),
  );

  const [defendantsAsInParent, setDefendantsAsInParent] = useState(
    parentClaim.defendants?.find((d) => !defendantIds.includes(d.id)) ===
      undefined,
  );

  const canSubmit = isValidClaimTitle(title);

  const availableDefendants = parties.filter(
    (party) => party.id !== parentClaim.claimant.id,
  );

  const handleDefendantsChange = (
    event: SelectChangeEvent<typeof defendantIds>,
  ) => {
    const {
      target: { value },
    } = event;
    setDefendantIds(
      // On autofill we get a stringified value.
      typeof value === "string" ? value.split(",") : value,
    );
  };

  const close = () => onClose();
  const submit = () => {
    if (defendantsAsInParent) {
      onClose({ title, defendants: parentClaim.defendants || [], generic });
      return;
    }
    const defendants = parties.filter((party) =>
      defendantIds.includes(party.id),
    );
    onClose({ title, defendants, generic });
  };

  // translate once, use twice
  const defendantsLabel = t(
    parties.length === 0 ? "label.noParties" : "label.defendants",
  );

  return (
    <DialogShell
      open={open}
      onClose={close}
      title={t("title", { parentClaim: parentClaim.title })}
      width={WIDTH}
      sectioned
      actions={
        <>
          <Button onClick={close} color="inherit">
            {t("cancel")}
          </Button>
          <Button onClick={submit} variant="contained" disabled={!canSubmit}>
            {t("submit")}
          </Button>
        </>
      }
    >
      <DialogSection
        first
        label={t("sections.title.label")}
        description={t("sections.title.description")}
      >
        <TextField
          autoFocus
          size="small"
          id="title"
          label={t("tip.title")}
          type="text"
          fullWidth
          required
          variant="outlined"
          value={title}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setTitle(event.target.value);
          }}
          error={!isValidClaimTitle(title)}
          helperText={
            !isValidClaimTitle(title) ? t("helperText.missingTitle") : undefined
          }
        />
      </DialogSection>
      <DialogSection
        label={t("sections.genericIssue.label")}
        description={t("sections.genericIssue.description", {
          claimant: parentClaim.claimant.name,
        })}
      >
        <FormControlLabel
          title={t("tip.genericIssue")}
          control={
            <Switch
              size="small"
              checked={generic === true}
              onChange={(e) => setGeneric(e.target.checked)}
            />
          }
          label={t("genericIssue")}
          labelPlacement="end"
          slotProps={{ typography: { variant: "body2" } }}
          sx={{ alignSelf: "flex-start", ml: 0, gap: 1 }}
        />
      </DialogSection>
      <DialogSection
        label={t("sections.defendants.label")}
        description={t("sections.defendants.description", {
          claimant: parentClaim.claimant.name,
        })}
      >
        <FormControlLabel
          title={t("tip.parentDefendants")}
          control={
            <Switch
              size="small"
              checked={defendantsAsInParent === true}
              onChange={(e) => setDefendantsAsInParent(e.target.checked)}
            />
          }
          disabled={generic}
          label={t("parentDefendants")}
          labelPlacement="end"
          slotProps={{ typography: { variant: "body2" } }}
          sx={{ alignSelf: "flex-start", ml: 0, gap: 1 }}
        />
        <Collapse in={defendantsAsInParent !== true}>
          <FormControl
            key={"defendants-selection"}
            fullWidth
            size="small"
            // room for the label, which sits on the upper line of the field
            sx={{ mt: 0.75 }}
            disabled={
              generic ||
              defendantsAsInParent ||
              availableDefendants.length === 0
            }
          >
            <InputLabel id="defendants-select-label">
              {defendantsLabel}
            </InputLabel>
            <Select
              multiple
              value={defendantIds}
              label={defendantsLabel}
              onChange={handleDefendantsChange}
              labelId="defendants-select-label"
              renderValue={(selected) =>
                parties
                  .filter((p) => selected.includes(p.id))
                  .map((p) => p.name)
                  .sort()
                  .join(", ")
              }
            >
              {availableDefendants
                .sort((p1, p2) => p1.name.localeCompare(p2.name))
                .map((party) => (
                  <MenuItem key={party.id} value={party.id}>
                    <Checkbox
                      size="small"
                      checked={defendantIds.includes(party.id)}
                    />
                    <ListItemText primary={party.name} />
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        </Collapse>
      </DialogSection>
    </DialogShell>
  );
};
